import { useMemo, useRef } from 'react'
import type { LineItem } from '@/hooks/useDocumentInvoiceData'
import {
  PDPurchaseOrder,
  PDPurchaseOrderLineItem,
  POLineItemMatch,
} from '@/types/po-match'

/** Reorder `items` to match `orderedIds`, appending any items not in the list. */
function reorderByIds<T extends { external_id: string }>(
  items: T[],
  orderedIds: string[],
): T[] {
  const map = new Map(items.map((item) => [item.external_id, item]))
  const ordered = orderedIds.map((id) => map.get(id)).filter((x): x is T => !!x)
  const orderedSet = new Set(orderedIds)
  return [
    ...ordered,
    ...items.filter((item) => !orderedSet.has(item.external_id)),
  ]
}

/**
 * Sorts PO line items so they visually align with matched invoice line items,
 * minimizing connector line crossings.
 *
 * Matched items are sorted by their matched invoice position and placed first.
 * Unmatched items are appended at the end in their original relative order.
 */
function sortPOLineItemsForAlignment(
  poLineItems: PDPurchaseOrderLineItem[],
  invoiceRowIds: string[],
  matchesForPO: POLineItemMatch[],
): PDPurchaseOrderLineItem[] {
  if (invoiceRowIds.length === 0) return poLineItems

  // For each matched PO line item, find the lowest matched invoice index
  const poItemInvoiceIndex = new Map<string, number>()
  matchesForPO.forEach((match) => {
    const invIndex = invoiceRowIds.indexOf(match.document_row_id)
    const existing = poItemInvoiceIndex.get(match.po_line_item_id)
    if (existing === undefined || invIndex < existing) {
      poItemInvoiceIndex.set(match.po_line_item_id, invIndex)
    }
  })

  // Separate matched and unmatched items so we can sort only the matched ones.
  // Sorting everything together would give unmatched items a fallback index of 0,
  // mixing them in with matched items at the top instead of keeping them at the end.
  const matched: PDPurchaseOrderLineItem[] = []
  const unmatched: PDPurchaseOrderLineItem[] = []
  poLineItems.forEach((li) => {
    if (poItemInvoiceIndex.has(li.external_id)) {
      matched.push(li)
    } else {
      unmatched.push(li)
    }
  })

  // Sort matched items by their matched invoice position so connector lines
  // don't cross. Unmatched items keep their original relative order at the end.
  matched.sort(
    (a, b) =>
      (poItemInvoiceIndex.get(a.external_id) ?? 0) -
      (poItemInvoiceIndex.get(b.external_id) ?? 0),
  )

  return [...matched, ...unmatched]
}

/**
 * Number-matching POs come first — the strongest signal we have that a PO
 * belongs to this invoice. Applied on every render rather than baked into the
 * locked order below, since such a PO can be pinned in after the first load.
 */
function hoistNumberMatches(
  purchaseOrders: PDPurchaseOrder[],
  poNumberMatchIds: Set<string>,
): PDPurchaseOrder[] {
  if (poNumberMatchIds.size === 0) return purchaseOrders

  const matched: PDPurchaseOrder[] = []
  const rest: PDPurchaseOrder[] = []
  purchaseOrders.forEach((po) => {
    if (poNumberMatchIds.has(po.external_id)) {
      matched.push(po)
    } else {
      rest.push(po)
    }
  })
  return [...matched, ...rest]
}

/**
 * The order the PO panel renders in: POs matching the invoice's PO number
 * first, then the order that minimizes connector line crossings — computed
 * once on first load and locked for the lifetime of the component.
 */
export default function usePODisplayOrder(
  purchaseOrders: PDPurchaseOrder[],
  invoiceLineItems: LineItem[],
  poLineItemMatchPerPoId: Map<string, POLineItemMatch[]>,
  isLoadingPOs: boolean,
  poNumberMatchIds: Set<string>,
): PDPurchaseOrder[] {
  const poOrderRef = useRef<string[] | null>(null)
  const lineItemOrderRef = useRef<Map<string, string[]> | null>(null)

  return useMemo(() => {
    const withMatchesFirst = (pos: PDPurchaseOrder[]) =>
      hoistNumberMatches(pos, poNumberMatchIds)

    if (isLoadingPOs) return withMatchesFirst(purchaseOrders)

    // Compute sort order once when data first loads
    if (poOrderRef.current === null && purchaseOrders.length > 0) {
      const invoiceRowIds = invoiceLineItems.map((li) => li.rowId)
      if (invoiceRowIds.length === 0) return withMatchesFirst(purchaseOrders)
      // Sort line items within each PO
      lineItemOrderRef.current = new Map(
        purchaseOrders.map((po) => {
          const matches = poLineItemMatchPerPoId.get(po.external_id) || []
          const sorted = sortPOLineItemsForAlignment(
            po.line_items,
            invoiceRowIds,
            matches,
          )
          return [po.external_id, sorted.map((li) => li.external_id)]
        }),
      )

      // Sort POs by their earliest matched invoice index
      poOrderRef.current = [...purchaseOrders]
        .sort((a, b) => {
          const aMatches = poLineItemMatchPerPoId.get(a.external_id) || []
          const bMatches = poLineItemMatchPerPoId.get(b.external_id) || []
          const aMin = Math.min(
            ...aMatches.map((m) => invoiceRowIds.indexOf(m.document_row_id)),
            Infinity,
          )
          const bMin = Math.min(
            ...bMatches.map((m) => invoiceRowIds.indexOf(m.document_row_id)),
            Infinity,
          )
          return aMin - bMin
        })
        .map((po) => po.external_id)
    }

    if (!poOrderRef.current || !lineItemOrderRef.current)
      return withMatchesFirst(purchaseOrders)

    // Lock in POs that appear after the initial sort so they keep their
    // appended position on later renders — the input order isn't stable
    // (poIds is sorted alphabetically), so we can't rely on it.
    const knownIds = new Set(poOrderRef.current)
    purchaseOrders.forEach((po) => {
      if (!knownIds.has(po.external_id)) {
        poOrderRef.current?.push(po.external_id)
      }
    })

    // Apply stored order, appending any POs/items added after initial load
    const ordered = reorderByIds(purchaseOrders, poOrderRef.current).map(
      (po) => {
        const storedOrder = lineItemOrderRef.current?.get(po.external_id)
        if (!storedOrder) return po
        return {
          ...po,
          line_items: reorderByIds(po.line_items, storedOrder),
        }
      },
    )
    return withMatchesFirst(ordered)
  }, [
    purchaseOrders,
    isLoadingPOs,
    invoiceLineItems,
    poLineItemMatchPerPoId,
    poNumberMatchIds,
  ])
}
